import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Link } from '@nerdwallet/currency';
import { itemsLinks, logoLink } from './data/brand-data';
import './sticky-header.less';

import {
  stringInputSpec,
  boolCheckboxSpec,
  repeatable,
  stringSelectSpec,
} from '../../lib/editing-configs';
import StickyHeaderBox from './sticky-header-box';

const StickyHeader = ({
  sections,
  usePreConfiguredNav,
  brandLogoUrl,
  awardsYear,
  shouldTrack,
}) => {
  const isBOA2023 = awardsYear === '2023';
  const menuItems = usePreConfiguredNav && isBOA2023 ? itemsLinks : sections;
  const stickyHeaderRef = useRef();
  const [display, setDisplay] = useState('none');

  // Sticky header should have a higher z-index, this makes editor panel tab hidden
  useEffect(() => {
    const stickyHeaderControl = document.querySelector(
      'div.editor-control__type.editor-control__type__stickyheader'
    );

    if (stickyHeaderControl) {
      // if panel tab is in the page we make it visible over sticky header
      stickyHeaderControl.style.zIndex = 102;
    }
  }, []);

  useEffect(() => {
    const boxTable = document.getElementById(
      'box-table-sticky-header-integration'
    );
    if (!boxTable) {
      setDisplay('flex');
    }
  }, []);

  return (
    <Box
      ref={stickyHeaderRef}
      id="sticky-header"
      className="sticky-header"
      style={{ display }}
    >
      <Box className="sticky-header__container">
        <Box className="sticky-header__sm-container">
          {brandLogoUrl && (
            <Link href={logoLink.url}>
              <img
                className="sticky-header__sm-logo"
                src={brandLogoUrl}
                alt="Nerdwallet Logo"
              />
            </Link>
          )}
        </Box>
        <Box className="sticky-header__items">
          {brandLogoUrl && (
            <Box className="brand-logo-wrapper">
              <Link href={logoLink.url}>
                <img
                  className="sticky-header__lg-logo"
                  src={brandLogoUrl}
                  alt="Nerdwallet Logo"
                />
              </Link>
            </Box>
          )}
          {menuItems.map((item) => (
            <StickyHeaderBox
              key={item.name}
              {...item}
              shouldTrack={shouldTrack}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

StickyHeader.propTypes = {
  brandLogoUrl: PropTypes.string,
  awardsYear: PropTypes.string,
  usePreConfiguredNav: PropTypes.bool,
  shouldTrack: PropTypes.bool,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ),
};

StickyHeader.defaultProps = {
  brandLogoUrl: '',
  awardsYear: '2023',
  sections: [],
  usePreConfiguredNav: false,
  shouldTrack: false,
};

StickyHeader.getEditingConfig = (componentDocument) => {
  const sectionsConfig = componentDocument?.props?.usePreConfiguredNav
    ? [
        stringSelectSpec('awardsYear', 'Awards Year', [
          { label: '2023', value: '2023' },
        ]),
      ]
    : [
        repeatable('sections', 'Sections', 'Section', () => {
          return [
            stringInputSpec('name', 'Name'),
            stringInputSpec('url', 'Section URL'),
          ];
        }),
      ];

  return [
    stringInputSpec('brandLogoUrl', 'Brand Logo Url'),
    boolCheckboxSpec('usePreConfiguredNav', 'Use pre-configured navigation?'),
    ...sectionsConfig,
    ...StickyHeaderBox.getEditingConfig(),
  ];
};

StickyHeader.displayName = 'StickyHeader';

export default StickyHeader;
